/**
 * AddressAutocomplete - A typeahead input for street addresses using Smarty.
 *
 * Features:
 * - Real-time address suggestions as user types (direct API call, no backend proxy)
 * - Auto-fills city, state (2-letter code), and ZIP when address is selected
 * - Returns USPS-verified addresses only
 * - Debounced API calls to minimize requests
 *
 * @see https://www.smarty.com/docs/cloud/us-autocomplete-pro-api
 */
import { useDebounce } from '@uidotdev/usehooks';
import { Loader2, MapPin, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  fetchAddressSuggestions,
  SmartySuggestion,
  toStandardAddress,
} from '@/services/smarty/smartyAddressService';

interface CompletedAddress {
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface AddressAutocompleteProps {
  /** Current street address value */
  value: string;
  /** Called when street address changes */
  onChange: (value: string) => void;
  /** Called when a full address is selected from suggestions */
  onAddressSelect?: (address: CompletedAddress) => void;
  /** Placeholder text */
  placeholder?: string;
  /** HTML id attribute */
  id?: string;
  /** Additional CSS classes for container */
  className?: string;
  /** Additional CSS classes for the input element */
  inputClassName?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Start typing an address...',
  id,
  className,
  inputClassName,
  disabled = false,
}) => {
  const [suggestions, setSuggestions] = useState<SmartySuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedValue = useDebounce(value, 300);

  // Fetch suggestions when input changes
  useEffect(() => {
    const loadSuggestions = async () => {
      if (!debouncedValue || debouncedValue.length < 3) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await fetchAddressSuggestions(debouncedValue);
        setSuggestions(results);
        setIsOpen(results.length > 0);
      } catch {
        // Silently fail - user can still type manually
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSuggestions();
  }, [debouncedValue]);

  // Handle suggestion selection
  const handleSelect = useCallback(
    (suggestion: SmartySuggestion) => {
      setIsOpen(false);
      setSelectedIndex(-1);

      // Build the full street line including secondary if present
      const streetLine = suggestion.secondary
        ? `${suggestion.street_line} ${suggestion.secondary}`
        : suggestion.street_line;

      onChange(streetLine);

      if (onAddressSelect) {
        onAddressSelect(toStandardAddress(suggestion));
      }
    },
    [onChange, onAddressSelect],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0) {
            handleSelect(suggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
        default:
          // Other keys are handled by the input field
          break;
      }
    },
    [isOpen, suggestions, selectedIndex, handleSelect],
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClear = useCallback(() => {
    onChange('');
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  }, [onChange]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={cn('pr-8', inputClassName)}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
          )}
          {value && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="text-zinc-400 hover:text-zinc-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-zinc-200 bg-white shadow-lg">
          <ul className="max-h-60 overflow-auto py-1">
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.street_line}-${suggestion.city}-${suggestion.zipcode}-${index}`}
                role="option"
                aria-selected={index === selectedIndex}
                tabIndex={-1}
                onClick={() => handleSelect(suggestion)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(suggestion);
                  }
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-3 py-2.5 text-sm',
                  index === selectedIndex
                    ? 'bg-zinc-100 text-zinc-900'
                    : 'text-zinc-700 hover:bg-zinc-50',
                )}
              >
                <MapPin className="h-4 w-4 flex-shrink-0 text-zinc-400" />
                <div className="flex-1 overflow-hidden">
                  <div className="truncate font-medium">
                    {suggestion.street_line}
                    {suggestion.secondary && (
                      <span className="ml-1 text-zinc-500">
                        {suggestion.secondary}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-zinc-500">
                    {suggestion.city}, {suggestion.state} {suggestion.zipcode}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
